import { inputIPV4AndV6Validator, deviceIdValidator, deviceModelValidator, clientAppVersionValidator, userAgentValidator, osVersionValidator } from '../../app/helpers/validators'

/**
 * Directive for URL policy rule.
 *
 * @class urlRule
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc directive
 * @name mwApp/common:urlRule
 *
 * @example
 * <url-rule entity="vm.urlPolicy" rules="vm.urlPolicy.$rules" config="vm.exrtra.config" form="form" is-view="vm.isView"></url-rule>
 * entity - model
 * rules - URL policy rules
 * config - rules config from extra
 * form - form
 * is-view - view|edit mode
 */
export function urlRule () {
  return {
    restrict: 'E',
    templateUrl: 'components/streams/views/urlRule.html',
    scope: {
      rule: '=rule',
      rules: '=rules',
      subRule: '=subRule',
      form: '=form',
      config: '=config',
      isView: '=isView',
      entity: '=entity'
    },
    link: function (scope) {
      scope.customNetworkValidators = inputIPV4AndV6Validator

      scope.addRule = addRule
      scope.propertiesNames = propertiesNames
      scope.customValidators = customValidators
      scope.operators = operators
      scope.comparisonOperators = comparisonOperators
      scope.isComparisonOperator = function (operator) {
        return comparisonOperators.includes(operator)
      }
      scope.getCustomValidators = function (criteria) {
        if (!criteria) return undefined

        scope._validatorsCache = scope._validatorsCache || {}

        const baseValidator = customValidators[criteria.property]
        if (!baseValidator) return undefined

        const isComparison = scope.isComparisonOperator(criteria.operator)
        const cacheKey = `${criteria.property}|${isComparison ? 'cmp' : 'noncmp'}`

        if (scope._validatorsCache[cacheKey]) {
          return scope._validatorsCache[cacheKey]
        }

        const isVersionProperty = ['client-app-version', 'os-version'].includes(criteria.property)
        const validatorsArray = Array.isArray(baseValidator) ? baseValidator : [baseValidator]
        let resultValidators = [...validatorsArray]

        if (isComparison && isVersionProperty) {
          // For comparison operators on version fields, use only numeric comparison validator
          resultValidators = [versionComparisonValidator]
        }

        scope._validatorsCache[cacheKey] = resultValidators
        return resultValidators
      }
      scope.configLength = Object.keys(scope.config).length
      scope.addCriteria = function (rule, type, property = newProperty) {
        if (rule.where.length < 2) { rule.operator = type }
        rule.where.push(angular.copy(property))
      }
      scope.delCriteria = function (rule) {

      }
      scope.getFilteredKeys = function (list, rules) {
        return (Array.isArray(list) ? list : Object.keys(list)).filter(key => !rules.includes(key))
      }
      scope.multiSelectWithAll = function (rules, model) {
        if (model.val === 'ALL') {
          rules = []
        } else {
          rules.push(model.val)
        }
        model.val = undefined
      }
      scope.deleteCriteria = function (rules, index) {
        rules.splice(index, 1)
      }
      scope.changeCriteria = function (criteria) {
        if (criteria.terms) { criteria.terms.length = 0 }
        criteria.operator = ''
      }
      scope.filteredProperties = function (properties, rule) {
        const selectedProperties = rule.where.map(criteria => criteria.property)
        return scope.getFilteredKeys(properties, selectedProperties)
      }

      scope.getDate = function () {
        return Date.now()
      }

      scope.$watch('rules', function (rules) {
        if (!Array.isArray(rules)) return

        rules.forEach(rule => {
          if (!rule?.where) return
          rule.where.forEach(criteria => {
            if (criteria?.terms && Array.isArray(criteria.terms) && scope.isComparisonOperator(criteria.operator) && criteria.terms.length > 1) {
              criteria.terms.splice(1)
            }
          })
        })
      }, true)
    }
  }
}
export const operators = {
  IS: 'Includes',
  IS_NOT: 'Excludes',
  ONE_OF: 'One Of',
  NONE_OF: 'None of',
  AND: 'And',
  OR: 'Or',
  SUBNET: 'One Of',
  HIGHER: 'Higher than',
  HIGHER_EQUALS: 'Higher or equal',
  LOWER: 'Lower than',
  LOWER_EQUALS: 'Lower or equal'
}
export const customValidators = {
  ip: inputIPV4AndV6Validator,
  'device-id': deviceIdValidator,
  'device-model': deviceModelValidator,
  'client-app-version': clientAppVersionValidator,
  'user-agent': userAgentValidator,
  'os-version': osVersionValidator
}
export const versionComparisonValidator = {
  name: 'versionComparisonValidator',
  fn: function (value) {
    if (!value) return true

    const isRegex = value.startsWith('^') && value.endsWith('$')
    if (isRegex) {
      return false
    }

    const numericPattern = /^[0-9. ]+$/
    return numericPattern.test(value)
  },
  errorText: 'Value must contain numbers and dots only for comparison operator'
}
export const comparisonOperators = ['HIGHER', 'HIGHER_EQUALS', 'LOWER', 'LOWER_EQUALS']
export const streamPolicyOperators = operators
export const propertiesNames = {
  platform: {
    name: 'Platform',
    items: {
      STB: { name: 'STB' },
      WEB: { name: 'WEB' },
      PC: { name: 'PC/LG' },
      LG: { name: 'LG' },
      iOS: { name: 'iOS' },
      TVOS: { name: 'tvOS' },
      Android: { name: 'Android' },
      SamsungTV: { name: 'Samsung TV' },
      Others: { name: 'Roku/Others' }
    }
  },
  os: {
    name: 'OS',
    items: {
      android: { name: 'Android' },
      ios: { name: 'iOS' },
      'tv-os': { name: 'tvOS' },
      'mac-os': { name: 'macOS' },
      windows: { name: 'Windows' },
      linux: { name: 'Linux' },
      'fire-os': { name: 'FireOS' },
      roku: { name: 'Roku' },
      tizen: { name: 'Tizen' },
      'web-os': { name: 'webOS' }
    }
  },
  'os-version': {
    name: 'OS version',
    tooltip: 'SemVer input,e.g; 5.0'
  },
  browser: {
    name: 'Browser',
    items: {
      chrome: { name: 'Chrome' },
      safari: { name: 'Safari' },
      'mobile-safari': { name: 'Mobile Safari' },
      firefox: { name: 'Firefox' },
      opera: { name: 'Opera' }
    }
  },
  'device-type': {
    name: 'Device Type',
    items: {
      tv: { name: 'TV' },
      stb: { name: 'STB' },
      mobile: { name: 'Mobile' },
      tablet: { name: 'Tablet' },
      desktop: { name: 'Desktop' }
    }
  },
  'device-model': {
    name: 'Device Model'
  },
  'device-id': {
    name: 'Device ID'
  },
  ip: { name: 'Network (IP/Subnet)' },
  'browser-version': { name: 'Browser Version' },
  'client-app-version': { name: 'Client App Version' },
  'user-agent': { name: 'User Agent' },
  'subscription-monetization-type': { name: 'Subscription Monetization Type' }
}
export const streamPolicyPropertiesNames = propertiesNames

export const newProperty = { property: '', operator: '', terms: [] }
export const newRule = {
  name: '',
  enabled: true,
  where: [angular.copy(newProperty)],
  operator: 'OR',
  $subRules: []
}

// export const deleteRule = (rules, index) => rules.splice(index, 1)
export const addRule = (rules, rule = newRule) => {
  rules.push(angular.copy(rule))
}
